import { saveRecord } from 'tio-data/requests/save-record';
import type { Store } from '@warp-drive/core';
import type {
  PslfAnnualAutoSubmitApproverKind,
  PslfAnnualAutoSubmitSource,
} from 'tio-data/data/resources/pslf-profile.type';
import type { PslfProfileExtension as PslfProfileModel } from 'tio-data/data/resources/pslf-profile.ext';
import type { PslfFormExtension as PslfFormModel } from 'tio-data/data/resources/pslf-form.ext';

/** How far ahead of the due date the dashboard starts counting down. */
export const ANNUAL_WINDOW_DAYS = 30;

/** Inside this many days the manual states escalate to the danger tier. */
export const ANNUAL_URGENT_DAYS = 7;

const MONTHS_IN_YEAR = 12;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Shared visual tier: `quiet` outside the window, `auto` scheduled, `warning`/`danger` manual. */
export type AnnualAutoSubmitTier = 'quiet' | 'auto' | 'warning' | 'danger';

export interface AnnualAutoSubmitInput {
  /** `pslfAnnualAutoSubmit`; false means the caller renders the pre-feature dashboard. */
  flagEnabled: boolean;
  /** `annualAutoSubmitOptedOut` — sticky, and `false` means enrolled. */
  optedOut: boolean;
  /** `annualAutoSubmitAvailable`; anything but `true` hides every annual surface. */
  available?: boolean | null;
  /** `annualAutoSubmitScheduledAt`; null whenever the job would pass this employee over. */
  scheduledAt?: string | null;
  /** `submittedAt` of the newest submitted current-employer form. */
  priorSubmittedAt?: string | null;
  source?: PslfAnnualAutoSubmitSource | null;
  /** Injected for tests and to keep every surface on one clock. */
  now?: Date;
}

export interface AnnualAutoSubmitStatus {
  flagEnabled: boolean;
  /** Flag on and the job could file for this employee, opt-out aside. Gates the settings surface. */
  available: boolean;
  /** Whether the countdown surfaces render: available, with a due date to count toward. */
  isActive: boolean;
  enrolled: boolean;
  /** Whether the job will act this cycle: available and enrolled, so a schedule came back. */
  eligible: boolean;
  dueDate: Date | null;
  /** Whole calendar days, floored at 0 once the due date has passed. */
  daysRemaining: number;
  isWithinWindow: boolean;
  isDueNow: boolean;
  tier: AnnualAutoSubmitTier;
  source: PslfAnnualAutoSubmitSource | null;
  /** A missing `approverKind` reads as `employerSigns`, the branch that promises least. */
  approverKind: PslfAnnualAutoSubmitApproverKind;
  /** Offer the opt-in promo: turned off, and close enough to feel the pain. */
  showOptInPromo: boolean;
}

function parseDate(value?: string | null): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function startOfLocalDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/** The anniversary the feature runs from: the prior submission plus twelve months; null when unparseable. */
export function annualAnniversaryFrom(submittedAt?: string | null): Date | null {
  const submitted = parseDate(submittedAt);
  if (!submitted) return null;

  const due = new Date(submitted.getTime());
  const day = due.getDate();
  due.setMonth(due.getMonth() + MONTHS_IN_YEAR);
  // Feb 29 rolls into March here, where ActiveSupport's `+ 1.year` clamps to Feb 28.
  if (due.getDate() !== day) due.setDate(0);
  return due;
}

/** ISO `submittedAt` of the newest submitted current-employer form — the anniversary anchor. */
export function newestCurrentEmployerSubmittedAt(forms: PslfFormModel[]): string | null {
  const submitted = forms
    .filter((form) => form.isCurrentEmployer && form.isSubmitted && form.submittedAt)
    .map((form) => form.submittedAt as string)
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

  return submitted[0] ?? null;
}

/** Whole calendar days from `now` to `dueDate`, never negative, so the count matches the date beside it. */
export function annualDaysRemaining(dueDate: Date | null, now: Date = new Date()): number {
  if (!dueDate) return 0;
  const diff = startOfLocalDay(dueDate) - startOfLocalDay(now);
  return Math.max(0, Math.round(diff / MS_PER_DAY));
}

function tierFor(
  eligible: boolean,
  enrolled: boolean,
  days: number,
  inWindow: boolean
): AnnualAutoSubmitTier {
  if (!inWindow) return 'quiet';
  if (enrolled && eligible) return 'auto';
  return days <= ANNUAL_URGENT_DAYS ? 'danger' : 'warning';
}

/** Resolve every annual surface from one input set; `isActive: false` means render the pre-feature UI. */
export function annualAutoSubmitStatus(input: AnnualAutoSubmitInput): AnnualAutoSubmitStatus {
  const now = input.now ?? new Date();
  const enrolled = !input.optedOut;
  const available = input.flagEnabled && input.available === true;
  const scheduledDate = parseDate(input.scheduledAt);
  const eligible = available && scheduledDate !== null;
  // An opted-out employee has no schedule but still needs a date to count toward, and only
  // the client knows the anniversary once the server has stopped computing one.
  const dueDate = scheduledDate ?? annualAnniversaryFrom(input.priorSubmittedAt);
  const isActive = available && dueDate !== null;
  const daysRemaining = annualDaysRemaining(dueDate, now);
  const isWithinWindow = isActive && daysRemaining <= ANNUAL_WINDOW_DAYS;

  return {
    flagEnabled: input.flagEnabled,
    available,
    isActive,
    enrolled,
    eligible,
    dueDate,
    daysRemaining,
    isWithinWindow,
    isDueNow: isActive && daysRemaining === 0,
    tier: isActive ? tierFor(eligible, enrolled, daysRemaining, isWithinWindow) : 'quiet',
    source: input.source ?? null,
    // Matches the API's own fallthrough, and over-promising final submission is the harmful direction.
    approverKind: input.source?.approverKind ?? 'employerSigns',
    showOptInPromo: isWithinWindow && !enrolled,
  };
}

/** Writes the sticky consent boolean; restores the previous value and rethrows if the PATCH is rejected. */
export async function setAnnualAutoSubmitOptedOut(
  store: Store,
  profile: PslfProfileModel,
  optedOut: boolean
) {
  const previous = profile.annualAutoSubmitOptedOut;
  profile.annualAutoSubmitOptedOut = optedOut;

  try {
    await saveRecord(store, profile);
  } catch (error) {
    profile.annualAutoSubmitOptedOut = previous;
    throw error;
  }
}
